import {
  ChangeDetectorRef,
  Component, computed,
  EventEmitter,
  inject,
  Input,
  model,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { Address, OpenQuoteRequestsItem, Quote } from '../quote-requests.types';
import { MatDivider } from '@angular/material/divider';
import { ReactiveFormsModule } from '@angular/forms';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { CurrencyPipe, DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { MatDialog } from '@angular/material/dialog';
import { FileUploaderModalComponent } from '../file-uploader-modal/file-uploader-modal.component';
import { FileUploadConfig } from '@sbd-doug-frontend/shared/lib/ui/file-upload/file-upload.component';
import { BondsListItem } from '../../../bonds/bonds-list/bonds-list.type';
import { createOpenQrMenuItems, DrawerMenuItem } from '../../../main-fuse-drawer/menus-buttons-template.types';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable,
} from '@angular/material/table';
import { QuoteRequestsListItem } from '../../../quote/requests-list/quote-request.type';
import { createShoppingCartItem } from '../../../../shopping-cart/shopping-cart.helpers';
import { CIPXDrawerManagerService } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-manager.service';
import { CIPXShoppingCartService } from '@sbd-doug-frontend/shared/lib/shopping-cart/shopping-cart.service';
import { CIPXRiderRequestService } from '../../../rider-request/rider-request.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { CIPXScrollableContentComponent } from '@sbd-doug-frontend/shared/lib/ui';
import { ActiveCommunicationContact } from '@sbd-doug-frontend/shared/lib/types/activecommunicationcontact.types';
import { activeCommunicationContactsFrom } from '@sbd-doug-frontend/shared/lib/types/active-communication-contacts';
import { MessageComposeDrawerWrapperComponent } from '../../../tasksets/message-compose/message-compose-drawer-wrapper.component';
import { UserService } from '@sbd-account-portal/app/core/user/user.service';
import { OpenQuoteRequestsWidgetService } from '../open-quote-requests-widget.service';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { QuoteRequestsListService } from '@sbd-account-portal/app/modules/quote/requests-list/quote-requests-list.service';
import { CIPXPrimaryButtonComponent } from '@sbd-doug-frontend/shared/lib/ui/buttons/primary-button/primary-button.component';
import { MatTooltip } from '@angular/material/tooltip';
import { SendReminderDialogComponent } from './send-reminder-dialog.component';
import { isServiceEmail } from '@sbd-doug-frontend/shared/lib/public-validators';

export type DrawerOpenerContext = 'Rider' | 'Bonds';

@Component({
  selector: 'sbd-account-portal-frontend-accordion-content',
  templateUrl: './accordion-content.component.html',
  styleUrl: './accordion-content.component.scss',
  imports: [
    MatDivider,
    ReactiveFormsModule,
    MatRadioGroup,
    MatExpansionPanelHeader,
    MatExpansionPanel,
    MatAccordion,
    NgClass,
    MatRadioButton,
    CurrencyPipe,
    DatePipe,
    NgForOf,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatIconButton,
    NgIf,
    MatHeaderCell,
    MatColumnDef,
    MatCell,
    MatCellDef,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatTable,
    MatRow,
    MatRowDef,
    MatButton,
    CIPXScrollableContentComponent,
    CIPXPrimaryButtonComponent,
    MatTooltip,
  ],
  standalone: true,
})

export class AccordionContentComponent implements OnInit {

  @Output() updatedOpenQR = new EventEmitter();
  @Output() cleanup = new EventEmitter<boolean>();
  @Output() fileUploaded = new EventEmitter<boolean>();
  @Output() riderRequested = new EventEmitter<boolean>();

  @Output() closeParentDrawerActionEvent = new EventEmitter<unknown>();
  @Output() getFileEvent = new EventEmitter<unknown>();
  @Input() item: OpenQuoteRequestsItem | BondsListItem | QuoteRequestsListItem = null;
  @Input() toggleAccordion = false;
  @Input() useAccordionComponent = true;
  @Input() fullDisplayWithoutAccordion = false;
  @Input() showSelectedDeliveryOption!: boolean;
  @Input() _cathyService: any;
  @Input() showStatus = false;

  @Input() fromInterface?: 'bonds' | 'quote-requests' | 'dashboard';

  isServiceEmail = isServiceEmail;
  _cathyService2 = inject(CathyService);
  _quoteRequestsListService = inject(QuoteRequestsListService);
  _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  _changeDetector = inject(ChangeDetectorRef);
  _userService = inject(UserService);

  readonly panelOpenState = signal(false);
  isScreenSmall = false;
  _unsubscribeAll: Subject<void> = new Subject<void>();
  menuItems: DrawerMenuItem[] = [];

  currentAccountUser = computed(() => this._userService.currentAccountUser());

  isAgency = computed(() => {
    const loggedInAccountUser = this._userService.currentAccountUser();
    return Boolean(loggedInAccountUser?.agency_id);
  });

  _drawerService = inject(CIPXDrawerManagerService);
  private _shoppingCartService = inject(CIPXShoppingCartService);
  private _riderRequestService = inject(CIPXRiderRequestService);
  _openQuoteRequestsWidgetService = inject(OpenQuoteRequestsWidgetService);

  riderSubscription$ = toObservable(this._riderRequestService.riderRequested)
    .pipe(takeUntil(this._unsubscribeAll))
    .subscribe((riderRequested) => {
      if (riderRequested) {
        this.riderRequested.emit(true);
        this._riderRequestService.riderRequested.set(false);
      }
    });

  constructor(public dialog: MatDialog) {
  }

  ngOnInit() {
    this.menuItems = createOpenQrMenuItems(this);

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });

  }

  onMenuItemClick(menuItem: DrawerMenuItem) {
    if (!menuItem.enabled) {
      return;
    }

    if (typeof menuItem.action === 'function') {
      menuItem.action();
    }
  }


  getDurationTerm(data: OpenQuoteRequestsItem | Quote) {
    if (Object.keys(data).filter(k => k === 'selected_quote').length > 0) {
      data = data as OpenQuoteRequestsItem;
      return data?.selected_quote?.duration + '-' + data?.selected_quote?.duration_units + ' Term';
    } else {
      data = data as Quote;
      if (data?.duration === undefined) {
        return 'Not defined';
      }
      return data?.duration + '-' + data?.duration_units + ' Term';
    }
  }

  getFormattedAddress(address: Address): string {
    if (!address) return '';

    let formattedAddress = '';
    if (address.street_1) {
      formattedAddress += address.street_1;
    }
    if (address.street_2) {
      formattedAddress += ', ' + address.street_2;
    }
    if (address.city) {
      formattedAddress += formattedAddress ? ', ' + address.city : address.city;
    }
    if (address.state_abbreviation) {
      formattedAddress += formattedAddress ? ', ' + address.state_abbreviation : address.state_abbreviation;
    }
    if (address.zip) {
      formattedAddress += formattedAddress ? ' ' + address.zip : address.zip;
    }
    return formattedAddress;
  }

  formatPhone(phone: string | null | undefined): string {
    if (!phone) return '';

    const digits = String(phone).replace(/\D/g, '');
    if (digits.length !== 10) {
      return phone;
    }

    const formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    // Only reformat when the display isn't already in the standard format.
    return /^\(\d{3}\)\s?\d{3}-\d{4}$/.test(phone.trim()) ? phone : formatted;
  }

  //If there is any change on the qupte option I am emitting to the parent the update object
  setSelectedQuoteOption() {
    const selectedQuote = this.item.quote_request_bond.quotes.findIndex((q, i) => q.premium === this.item.quote_selection_form.get('selected_quote_option').value);
    this.item.selected_quote = this.item.quote_request_bond.quotes[selectedQuote];
    this.updatedOpenQR.emit(this.item);
  }

  protected readonly isNaN = isNaN;

  closeParentDrawerAction() {
    this.cleanup.emit(true);
  }

  getFile(quoteRequestId: any, file_id: number) {
    this.getFileEvent.emit({ quoteRequestId, file_id });
  }

  openFileUploader() {
    const dialogData = {
      context: 'cipquotereq',
      id: this.item.quote_request_id,
      uploadConfig: <FileUploadConfig>{
        caption: '<p class="text-md">Drag Files Here or Click to Browse</p>',
        noButton: true,
        noIcon: false,
        horizontal: true,
      },
      defaultUploadConfig: <FileUploadConfig>{
        caption: '<p class="text-md">Drag Files Here or Click to Browse</p>',
        noButton: true,
        noIcon: false,
        horizontal: true,
      },
    };

    const dialogRef = this.dialog.open(FileUploaderModalComponent, {
      data: { ...dialogData },
      width: '20vw',
      height: '200pxvw',
      maxWidth: '80vw',
      maxHeight: '70vw',
      minHeight: '200px',
      minWidth: '300px',
    });

    dialogRef.afterClosed().subscribe(result => {
      this.fileUploaded.emit(true);
      if (result) {
        // this._customerUsersService.submitCustomerUserForm.set(true)
        // this.openUserMergeDialog(true)
      }
    });
    return;
  }

  currentOpenQRIsPurchasable() {
    return +this.item.customer_can_purchase === 1;
  }

  currentOpenQRIsPurchased() {
    return this.item.quote_status_stage === 'Issued';
  }

  currentOpenQRIsManaged() {
    return this.item.is_managed === true;
  }

  currentOpenQRCanDecline() {
    return this.item.quote_status_stage?.toLowerCase() === 'selling';
  }

  currentOpenQRIsIssued() {
    return this.item.quote_status_stage?.toLowerCase() === 'issued';
  }

  currentOpenQRCanRequestChange() {
    return this.item.quote_status_stage?.toLowerCase() === 'selling' || this.item.quote_status_stage?.toLowerCase() === 'quoting';
  }



  getFileIcon(extension: string): string {
    const iconMap = {
      'pdf': 'picture_as_pdf',
      'doc': 'description',
      'docx': 'description',
      'jpg': 'image',
      'png': 'image',
    };

    return iconMap[extension] || 'insert_drive_file';
  }


  buyNow(item: any) {
    this._drawerService.closeAllDrawers();
    this._shoppingCartService.addItem(createShoppingCartItem(item));
    this._shoppingCartService.openDrawer();
  }

  openNewDrawer(context: DrawerOpenerContext) {
    switch (context) {
      case 'Rider':
        this._riderRequestService.item.set(this.item);
        this._riderRequestService.openDrawer();
        break;
    }
  }



  sendMessageFromDrawer() {
    const subject = `Question about order ${this.item?.order_number}`;
    const principal = this.item?.company_name || `${this.item?.user_first_name ?? ''} ${this.item?.user_last_name ?? ''}`.trim();
    const formatUSD = (v: any) => {
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : String(v ?? '');
    };
    const penalty = formatUSD(this.item?.bond_amount ?? this.item?.quote_request_bond?.bond_amount);
    const state = this.item?.state_abbreviation ?? this.item?.quote_request_bond?.state_abbreviation;
    const suretyLabel = this.item?.surety_bond_label ?? this.item?.quote_request_bond?.surety_bond_label;
    const body = `<br><br>Regarding ${penalty}, ${state}, ${suretyLabel} for ${principal}`;
    const t = {
      task_label: subject,
      task_description: body,
      task_set_id: (this.item as any)?.task_set_id ?? null,
      assignees: [],
      task_related_entity_key: this.item.quote_request_id,
      task_related_entity_type: 'cipquotereq',
    } as any;

    this._drawerService.openDrawer(MessageComposeDrawerWrapperComponent, {
      data: {
        title: `Send Message · Order ${this.item?.order_number}`,
        qr: this.item as any,
        task: t,
      },
    });
  }

  sendMessageFromDrawerMakeAChange() {
    const principal = this.item?.quote_request_bond?.company_name || `${this.item?.user_first_name ?? ''} ${this.item?.user_last_name ?? ''}`.trim();
    const subject = `Change needed for ${principal} ${this.item?.order_number}`;
    const formatUSD = (v: any) => {
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : String(v ?? '');
    };
    const penalty = formatUSD(this.item?.bond_amount ?? this.item?.quote_request_bond?.bond_amount);
    const state = this.item?.state_abbreviation ?? this.item?.quote_request_bond?.state_abbreviation;
    const suretyLabel = this.item?.surety_bond_label ?? this.item?.quote_request_bond?.surety_bond_label;
    const body = `<br><br>Regarding ${penalty}, ${state}, ${suretyLabel} for ${principal}`;
    const t = {
      task_label: subject,
      task_description: body,
      task_set_id: (this.item as any)?.task_set_id ?? null,
      assignees: [],
      task_related_entity_key: this.item.quote_request_id,
      task_related_entity_type: 'cipquotereq',
    } as any;

    this._drawerService.openDrawer(MessageComposeDrawerWrapperComponent, {
      data: {
        title: `Send Message · Order ${this.item?.order_number}`,
        qr: this.item as any,
        task: t,
      },
    });
  }

  declineQuote() {
    this._openQuoteRequestsWidgetService.declineQuote(+this.item.quote_request_id).subscribe((response) => {
      if (response && response.quote_request) {
        this._openQuoteRequestsWidgetService.loadQuoteRequest(+this.item.quote_request_id).subscribe((r) => {
          if (r) {
            this._openQuoteRequestsWidgetService.selectedOpenQR.set(r);
            this.item = r;
            this.updatedOpenQR.emit(this.item);
            this._cathyService2?.success('Quote declined successfully');
            this._openQuoteRequestsWidgetService.getOpenQuoteRequests().subscribe();
            this._quoteRequestsListService.triggerRefresh.emit();
            this._changeDetector.detectChanges();
          } else {
            this._cathyService2?.error('Failed to decline quote');
          }
        });
      }
    });
  }

  // ----- Communications Helpers -----
  activeCommunicationContacts(): Array<ActiveCommunicationContact> {
    const cu = this.currentAccountUser() as { user_id?: number | string; id?: string; email?: string } | null;
    const item = this.item as OpenQuoteRequestsItem;
    return activeCommunicationContactsFrom(item?.communication, {
      customerUserId: Number(item?.user_id),
      accountUserId: Number(cu?.user_id ?? cu?.id) || null,
      accountUserEmail: cu?.email,
      isRenewal: Number(item?.renewal_quote_request_id) > 0,
    });
  }


  isMissingEmail(email: string): boolean {
    if (!email) return true;
    const lowerEmail = email.toLowerCase();
    return lowerEmail.includes('service+missing') || lowerEmail.includes('service+max');
  }

  isOverdue(): boolean {
    if (!this.item?.selected_quote?.bond_effective_date) return false;
    const dueDate = new Date(this.item.selected_quote.bond_effective_date);
    const tenDaysBefore = new Date(dueDate.getTime());
    tenDaysBefore.setDate(dueDate.getDate() - 10);
    return tenDaysBefore.getTime() > Date.now();
  }

  getReminderMailto(c: ActiveCommunicationContact): string {
    // Placeholder (service+) addresses aren't deliverable; the dialog disables
    // Send Email and the account user can copy the purchase URL instead
    if (!c.email || this.isMissingEmail(c.email)) return '';

    const isRenewal = (this.item as any)?.renewal_quote_request_id > 0;
    const bondAmount = this.item?.quote_request_bond?.bond_amount
      ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(this.item.quote_request_bond.bond_amount)
      : '';
    const suretyBondLabel = `${bondAmount} ${this.item?.quote_request_bond?.state_abbreviation || ''} ${this.item?.quote_request_bond?.surety_bond_label || ''}`.trim();
    const orderNumber = this.item?.order_number || '';
    const dueDateStr = this.item?.selected_quote?.bond_effective_date ? new Date(this.item.selected_quote.bond_effective_date).toLocaleDateString() : '';
    const jumpUrl = this.item?.selected_quote?.jump_url || '';

    const subject = `Reminder to ${isRenewal ? 'renew' : 'purchase'} your ${suretyBondLabel} #${orderNumber}`;
    let body = '';

    const greetingName = c.name.split(' ')[0] || 'Customer';

    if (isRenewal) {
      const overdueText = this.isOverdue() ? ' which is now overdue' : '';
      body = `${greetingName},\nPlease use the link below to go to Surety Bonds Direct to pay for your ${suretyBondLabel} renewal effective ${dueDateStr}${overdueText}.\n${jumpUrl}`;
    } else {
      body = `${greetingName},\nPlease use the link below to go to Surety Bonds Direct to purchase your ${suretyBondLabel}.\n${jumpUrl}`;
    }

    return `mailto:${c.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  getGlobalReminderMailto(): string {
    const contacts = this.activeCommunicationContacts();
    const currentUserEmail = this.currentAccountUser()?.email;

    const filteredContacts = contacts.filter(c => c.email && c.email !== currentUserEmail && !this.isMissingEmail(c.email));

    if (filteredContacts.length === 0) return '';

    const toContacts = filteredContacts.filter(c => c.contact_type !== 'listener');
    const ccContacts = filteredContacts.filter(c => c.contact_type === 'listener');

    const toEmails = toContacts.map(c => c.email).join(',');
    const ccEmails = ccContacts.map(c => c.email).join(',');

    const isRenewal = (this.item as any)?.renewal_quote_request_id > 0;
    const bondAmount = this.item?.quote_request_bond?.bond_amount
      ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(this.item.quote_request_bond.bond_amount)
      : '';
    const suretyBondLabel = `${bondAmount} ${this.item?.quote_request_bond?.state_abbreviation || ''} ${this.item?.quote_request_bond?.surety_bond_label || ''}`.trim();
    const orderNumber = this.item?.order_number || '';
    const dueDateStr = this.item?.selected_quote?.bond_effective_date ? new Date(this.item.selected_quote.bond_effective_date).toLocaleDateString() : '';
    const jumpUrl = this.item?.selected_quote?.jump_url || '';

    const subject = `Reminder to ${isRenewal ? 'renew' : 'purchase'} your ${suretyBondLabel} #${orderNumber}`;
    let body = '';

    const greetingName = toContacts.length > 0 ? toContacts[0].name.split(' ')[0] : 'Customer';

    if (isRenewal) {
      const overdueText = this.isOverdue() ? ' which is now overdue' : '';
      body = `${greetingName},\nPlease use the link below to go to Surety Bonds Direct to pay for your ${suretyBondLabel} renewal effective ${dueDateStr}${overdueText}.\n${jumpUrl}`;
    } else {
      body = `${greetingName},\nPlease use the link below to go to Surety Bonds Direct to purchase your ${suretyBondLabel}.\n${jumpUrl}`;
    }

    let mailto = `mailto:${toEmails}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    if (ccEmails) {
      mailto += `&cc=${ccEmails}`;
    }

    return mailto;
  }

  hasRecipient(): boolean {
    const contacts = this.activeCommunicationContacts();
    const currentUserEmail = this.currentAccountUser()?.email;
    return contacts.some(c => c.email && c.email !== currentUserEmail && !this.isMissingEmail(c.email));
  }

  openMailto(mailto: string) {
    window.location.href = mailto;
  }

  openSendReminderDialog(contact: ActiveCommunicationContact) {
    const mailto = this.getReminderMailto(contact);
    const jumpUrl = this.item?.selected_quote?.jump_url || '';

    this.dialog.open(SendReminderDialogComponent, {
      data: { mailto, jumpUrl, contactName: contact.name },
      width: '360px',
      autoFocus: false,
    });
  }

  getPaymentAmountForCurrentOrder(payment: any): number {
    if (!payment || !this.item?.order_number) {
      return payment?.payment_amount;
    }

    if (payment.concerned_order_numbers && Array.isArray(payment.concerned_order_numbers)) {
      const currentOrder = payment.concerned_order_numbers.find(
        (o: any) => o.order_number === this.item.order_number
      );
      if (currentOrder && currentOrder.invoice_amount !== undefined) {
        return currentOrder.invoice_amount;
      }
    }

    return payment.payment_amount;
  }
}

